/**
 * useConversation — single source of truth for one chat conversation.
 *
 * Ported verbatim from `apps/agent/client/src/hooks/use-conversation.ts`
 * (commit 821da7a6, 802 L). Path-fixups only — `@/lib/fetchers` →
 * `../lib/api-client`, `@/lib/socket-client` → `../lib/socket-client`.
 *
 * Owns:
 *   - SWR-loaded persisted conversation + messages
 *   - Socket.io `conv:<id>` subscription (start, queued, token, tool_use,
 *     tool_result, complete, error, cancelled, approval_request,
 *     card_request, card_resolved, conversation:title)
 *   - Optimistic user-message append + reconcile to real id on 202
 *   - Watchdog (7s mobile / 45s desktop) on stuck `awaiting`
 *   - Reconnect resilience (re-emit host:join-conversation on connect)
 *   - Persistent approvals (parallel stacking) + resolved-card remnants
 *   - Lazy-load older messages via `?before=<id>&limit=50`
 *   - Cancel + decideApproval + resolveCard
 *
 * Per `apps/agent/plans/plan-08-conversations-and-streaming.md`,
 * `plan-turn-progress-feedback.md`, `plan-action-cards.md`,
 * `plan-chat-scroll-lazy-load.md`, `plan-allhands-approvals-and-date.md`,
 * `plan-allhands-2-cards-and-brand.md`.
 */

import type { ActionCard, ActionCardResolution } from '@ordo/agent-tools'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useSWR, { mutate as globalMutate } from 'swr'

import { fetcher, getApiBase } from '../lib/api-client'
import { getAgentToken } from '../lib/socket-client'
import { getEmbedSocket, onSocketConnectedChange } from '../lib/socket-client'
import type {
  ChatApprovalRequest,
  ChatAttachment,
  ChatCitation,
  ChatMessage,
  ChatToolCall,
  MessageRole,
  PendingChatCard,
  ResolvedChatCard,
  TurnState,
} from '../types'
import { mergeMissingUserMessages } from './merge-missing-user-messages'

interface PersistedMessage {
  id: string
  role: string
  content: string
  toolCalls?: ChatToolCall[] | null
  citations?: ChatCitation[] | null
  attachments?: ChatAttachment[] | null
  suggestedReplies?: string[] | null
  tokensIn?: number | null
  tokensOut?: number | null
  costUsd?: string | number | null
}

interface PersistedConversation {
  id: string
  title: string | null
  hostApp: string | null
  messages: PersistedMessage[]
  hasMore?: boolean
}

const apiPathFor = (id: string | null) => (id ? `/api/recall/conversations/${id}` : null)

/** Build auth headers for direct fetch calls. Cross-origin embeds
 *  rely on a Bearer token (configured via `configureAgentToken`)
 *  because the browser won't send the `.ordo.com` cookie across
 *  origins. Same-origin embeds fall through to cookie auth. Without
 *  this header on conversation-create / presign, cross-origin
 *  embeds 401 silently and image uploads never reach S3 (ticket
 *  230fc57d / plan-03). */
async function authHeaders(): Promise<Record<string, string>> {
  const token = await getAgentToken()
  return token ? { authorization: `Bearer ${token}` } : {}
}

function urlOf(path: string): string {
  const base = getApiBase()
  return base ? `${base}${path}` : path
}

/** Categorised send/create error so the UI can decide how to render
 *  + whether to surface a one-time toast. */
export type AgentSendErrorCategory = 'network' | 'auth' | 'server' | 'backend' | 'unknown'

export class AgentSendError extends Error {
  readonly category: AgentSendErrorCategory
  readonly status?: number
  /** Engine-level retry context — message id this error pertains to,
   *  so the UI can offer a per-message Retry affordance. */
  readonly messageId?: string
  constructor(opts: { category: AgentSendErrorCategory; message: string; status?: number; messageId?: string }) {
    super(opts.message)
    this.name = 'AgentSendError'
    this.category = opts.category
    if (opts.status !== undefined) {
      this.status = opts.status
    }
    if (opts.messageId !== undefined) {
      this.messageId = opts.messageId
    }
  }
}

function classifyHttpStatus(status: number): AgentSendErrorCategory {
  if (status === 401 || status === 403) {
    return 'auth'
  }
  if (status === 404) {
    return 'backend'
  }
  if (status >= 500) {
    return 'server'
  }
  return 'unknown'
}

/** User-facing message rendered on the errored chat bubble. Short
 *  enough to fit under a message; full detail is on the toast. */
function errorMessageFor(error: AgentSendError): string {
  switch (error.category) {
    case 'network':
      return 'Network unavailable'
    case 'auth':
      return 'Sign-in required'
    case 'backend':
      return "Couldn't reach the agent"
    case 'server':
      return 'Server error'
    default:
      return 'Failed to send'
  }
}

export interface UseConversationResult {
  conversation: PersistedConversation | null
  messages: ChatMessage[]
  hasMore: boolean
  isLoadingOlder: boolean
  loadOlder: () => Promise<void>
  isStreaming: boolean
  turnState: TurnState
  isBusy: boolean
  isLoading: boolean
  /**
   * [PROD-1044] The tool the agent is running right now, or null between tools.
   * Drives the in-progress bar's label ("Searching Tasks…" instead of a static
   * "Generating…"). Pool turns render tool calls ONLY through this — they
   * deliberately never materialize raw tool cards for non-technical users.
   */
  liveActivity: { slug: string; args: unknown } | null
  send: (
    content: string,
    hostApp?: string,
    attachmentIds?: string[],
    mode?: 'ask' | 'agent',
    slashCommand?: { trigger: string; label: string },
    hostContext?: Record<string, unknown>,
    attachments?: ChatAttachment[],
  ) => Promise<string>
  /** Re-attempt sending a previously-errored user message. Looks up
   *  the message by id, flips it back to 'streaming' (clearing the
   *  error), re-posts to the backend with the same content +
   *  attachments. The agent's stream populates the assistant reply
   *  via the existing socket subscription. */
  retry: (messageId: string) => Promise<void>
  cancel: () => Promise<void>
  decideApproval: (toolUseId: string, decision: 'approve' | 'skip') => Promise<void>
  resolveCard: (cardId: string, resolution: ActionCardResolution) => Promise<void>
  ensureConversation: () => Promise<string | null>
  conversationId: string | null
  /** Categorized last error (cleared on next successful send). Used
   *  by the surface to fire a one-time `showActionToast` for terminal
   *  conditions (auth expired, backend unreachable). */
  lastError: AgentSendError | null
}

export function useConversation(conversationId: string | null): UseConversationResult {
  const [activeId, setActiveId] = useState<string | null>(conversationId)
  useEffect(() => {
    setActiveId(conversationId)
  }, [conversationId])

  const { data, mutate, isLoading } = useSWR<PersistedConversation>(apiPathFor(activeId), fetcher, {
    keepPreviousData: true,
  })
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [isLoadingOlder, setIsLoadingOlder] = useState(false)
  const [turnState, setTurnState] = useState<TurnState>('idle')
  // [PROD-1044] Last `message:heartbeat` from the server — "this turn is alive,
  // just quiet". A pool turn spends its whole cold-boot window (session create +
  // pod slot) silent on the wire, which the awaiting watchdog below read as a
  // dead stream: it hid the "Generating…" bar (and its Stop button) while the
  // request was very much still in flight, so users re-prompted.
  const [streamAliveAt, setStreamAliveAt] = useState(0)
  // [PROD-1044] The tool running right now (pool turns' only tool signal).
  const [liveActivity, setLiveActivity] = useState<{ slug: string; args: unknown } | null>(null)
  // Clear Chat sets conversationId → null. The seed effect below
  // bails on `data.id !== activeId`, so without this reset the old
  // messages would linger on screen until a refresh. Wipe local
  // turn state the moment the active conversation is dropped so the
  // next user send mints a fresh thread cleanly.
  useEffect(() => {
    if (activeId !== null) {
      return
    }
    setMessages([])
    setHasMore(false)
    setIsLoadingOlder(false)
    setTurnState('idle')
  }, [activeId])
  const isStreaming = turnState === 'streaming'
  const isBusy = turnState !== 'idle'
  const conversationIdRef = useRef(activeId)
  conversationIdRef.current = activeId
  // Mirror messages so the retry callback can find the preceding
  // user message without re-creating itself on every keystroke.
  const messagesRef = useRef<ChatMessage[]>([])
  messagesRef.current = messages

  // Track which conversation has already had its server snapshot
  // seeded into local state. See the matching comment in the host
  // app's hooks/use-conversation.ts — without this, every SWR
  // refetch re-seeded messages and clobbered live socket-driven
  // state (e.g. a freshly-emitted PlanCard's pendingCard), which
  // showed up as the plan card requiring a page refresh to become
  // interactive. Ticket 905331f4 / plan-02.
  const seededConvIdRef = useRef<string | null>(null)

  // Seed local messages from server data on convo change. Hydrate
  // persisted cards (RecallMessage.cards) into pendingApprovals[],
  // pendingCard, and resolvedCards[] per
  // `apps/agent/plans/plan-allhands-2-cards-and-brand.md`.
  useEffect(() => {
    if (!data || data.id !== activeId) {
      return
    }
    if (seededConvIdRef.current === activeId) {
      // Already seeded — refetches defer to the socket for live state,
      // but additively fold in persisted user rows we don't have yet
      // (e.g. attachments finalized server-side after the seed).
      const merged = mergeMissingUserMessages(messagesRef.current, data.messages)
      if (merged) {
        setMessages(merged)
      }
      return
    }
    seededConvIdRef.current = activeId
    const seeded: ChatMessage[] = data.messages.map((m) => {
      const persistedCards = (
        Array.isArray((m as { cards?: unknown }).cards) ? (m as unknown as { cards: unknown[] }).cards : []
      ) as Array<{
        cardId: string
        kind: string
        card: unknown
        status: 'pending' | 'resolved'
        resolution?: unknown
        toolUseId?: string
      }>
      const pendingApprovals: ChatApprovalRequest[] = []
      let pendingCard: PendingChatCard | null = null
      const resolvedCards: ResolvedChatCard[] = []
      for (const c of persistedCards) {
        if (c.kind === 'confirm') {
          if (c.status === 'pending') {
            const card = c.card as { slug?: string; args?: unknown; rationale?: string }
            pendingApprovals.push({
              toolUseId: c.toolUseId ?? c.cardId,
              slug: card.slug ?? '',
              args: card.args,
              rationale: card.rationale,
            })
          } else {
            resolvedCards.push({
              cardId: c.cardId,
              toolUseId: c.toolUseId ?? c.cardId,
              card: c.card as ActionCard,
              resolution: c.resolution as ActionCardResolution,
            })
          }
        } else if (c.kind === 'agent_question' || c.kind === 'agent_plan') {
          // [FACTORY-447] The inline question/plan entries a pool-backed turn
          // persists. The embed doesn't render them; falling through would mount
          // them as a broken ActionCard.
          continue
        } else if (c.status === 'pending') {
          pendingCard = {
            cardId: c.cardId,
            toolUseId: c.toolUseId ?? c.cardId,
            card: c.card as ActionCard,
          }
        } else {
          resolvedCards.push({
            cardId: c.cardId,
            toolUseId: c.toolUseId ?? c.cardId,
            card: c.card as ActionCard,
            resolution: c.resolution as ActionCardResolution,
          })
        }
      }
      return {
        id: m.id,
        role: m.role.toLowerCase() === 'user' ? 'user' : 'assistant',
        content: m.content,
        toolCalls: Array.isArray(m.toolCalls) ? m.toolCalls : undefined,
        citations: Array.isArray(m.citations) ? m.citations : undefined,
        attachments: Array.isArray(m.attachments) ? m.attachments : undefined,
        pendingApprovals: pendingApprovals.length > 0 ? pendingApprovals : undefined,
        pendingCard,
        resolvedCards: resolvedCards.length > 0 ? resolvedCards : undefined,
        suggestedReplies:
          Array.isArray(m.suggestedReplies) && m.suggestedReplies.length > 0 ? m.suggestedReplies : undefined,
        status: 'complete' as const,
        tokensIn: m.tokensIn ?? undefined,
        tokensOut: m.tokensOut ?? undefined,
        costUsd: typeof m.costUsd === 'string' ? Number(m.costUsd) : (m.costUsd ?? undefined),
      }
    })
    setMessages(seeded)
    setHasMore(Boolean(data.hasMore))
  }, [data, activeId])

  // Subscribe to streaming events for the active conversation.
  useEffect(() => {
    if (!activeId) {
      return
    }
    const socket = getEmbedSocket()
    socket.emit('host:join-conversation', { conversationId: activeId })

    const unsubscribeConnected = onSocketConnectedChange((connected) => {
      if (connected && conversationIdRef.current === activeId) {
        socket.emit('host:join-conversation', { conversationId: activeId })
      }
    })

    const onStart = (e: { messageId: string; role: MessageRole }) => {
      setTurnState('streaming')
      // [PROD-1044] A fresh turn owns a fresh label. Clearing at turn START
      // (rather than at each of the dozen paths back to 'idle', watchdogs and
      // disconnects included) is the one place every turn routes through, so no
      // exit can leak a previous turn's tool label into this bar.
      setLiveActivity(null)
      // Idempotent — the server's join-replay (FACTORY-294) re-emits start for
      // a row the seed already rendered; don't duplicate the bubble.
      setMessages((prev) =>
        prev.some((m) => m.id === e.messageId)
          ? prev
          : [...prev, { id: e.messageId, role: e.role, content: '', status: 'streaming' }],
      )
    }
    const onQueued = () => {
      setTurnState((prev) => (prev === 'sending' ? 'awaiting' : prev))
    }
    const onToken = (e: { messageId: string; delta: string }) => {
      setMessages((prev) => prev.map((m) => (m.id === e.messageId ? { ...m, content: m.content + e.delta } : m)))
    }
    const onToolUse = (e: { messageId: string; toolUseId: string; slug: string; args: unknown; position?: number }) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== e.messageId) {
            return m
          }
          const calls = m.toolCalls ?? []
          const position = e.position ?? m.content.length
          return {
            ...m,
            toolCalls: [...calls, { id: e.toolUseId, slug: e.slug, args: e.args, position }],
          }
        }),
      )
    }
    const onToolResult = (e: {
      messageId: string
      toolUseId: string
      status: ChatToolCall['status']
      citations?: ChatCitation[]
      errorMessage?: string
    }) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== e.messageId) {
            return m
          }
          const calls = (m.toolCalls ?? []).map((c) =>
            c.id === e.toolUseId ? { ...c, status: e.status, citations: e.citations ?? c.citations } : c,
          )
          return { ...m, toolCalls: calls }
        }),
      )
    }
    const onComplete = (e: {
      messageId: string
      usage: { tokensIn: number; tokensOut: number; costUsd: number }
      // Plan-30: server now sends the cleaned content + parsed chips
      // on stream-end so the live bubble matches the persisted shape
      // without requiring a refresh. Optional — older servers omit them.
      content?: string
      suggestedReplies?: string[]
    }) => {
      setTurnState('idle')
      setLiveActivity(null)
      setMessages((prev) =>
        prev.map((m) =>
          m.id === e.messageId
            ? {
                ...m,
                status: 'complete',
                tokensIn: e.usage.tokensIn,
                tokensOut: e.usage.tokensOut,
                costUsd: e.usage.costUsd,
                ...(typeof e.content === 'string' ? { content: e.content } : {}),
                ...(Array.isArray(e.suggestedReplies) ? { suggestedReplies: e.suggestedReplies } : {}),
              }
            : m,
        ),
      )
      void mutate()
    }
    const onError = (e: { messageId: string; message: string }) => {
      setTurnState('idle')
      setLiveActivity(null)
      setMessages((prev) =>
        prev.map((m) => (m.id === e.messageId ? { ...m, status: 'error', errorMessage: e.message } : m)),
      )
    }
    const onCancelled = (e: { messageId: string }) => {
      setTurnState('idle')
      setLiveActivity(null)
      setMessages((prev) =>
        prev.map((m) =>
          m.id === e.messageId ? { ...m, status: 'complete', cancelled: true, pendingApprovals: [] } : m,
        ),
      )
      void mutate()
    }
    const onApprovalRequest = (e: {
      messageId: string
      toolUseId: string
      slug: string
      args: unknown
      rationale?: string
    }) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== e.messageId) {
            return m
          }
          const existing = m.pendingApprovals ?? []
          if (existing.some((p) => p.toolUseId === e.toolUseId)) {
            return m
          }
          return {
            ...m,
            pendingApprovals: [
              ...existing,
              { toolUseId: e.toolUseId, slug: e.slug, args: e.args, rationale: e.rationale },
            ],
          }
        }),
      )
    }
    const clearApproval = (e: { messageId: string; toolUseId: string; decision?: 'approve' | 'skip' }) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== e.messageId) {
            return m
          }
          const removed = (m.pendingApprovals ?? []).find((p) => p.toolUseId === e.toolUseId)
          const filtered = (m.pendingApprovals ?? []).filter((p) => p.toolUseId !== e.toolUseId)
          if (!removed) {
            return m
          }
          const remnant: ResolvedChatCard = {
            cardId: e.toolUseId,
            toolUseId: e.toolUseId,
            card: {
              kind: 'confirm',
              attribution: removed.slug,
              summary: undefined,
              rationale: removed.rationale,
              destructive: false,
            } as unknown as ActionCard,
            resolution: { kind: 'confirm', decision: e.decision ?? 'approve' } as unknown as ActionCardResolution,
          }
          return {
            ...m,
            pendingApprovals: filtered,
            resolvedCards: [...(m.resolvedCards ?? []), remnant],
          }
        }),
      )
    }

    const onCardRequest = (e: { messageId: string; toolUseId: string; cardId: string; card: ActionCard }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === e.messageId ? { ...m, pendingCard: { cardId: e.cardId, toolUseId: e.toolUseId, card: e.card } } : m,
        ),
      )
    }

    const onCardResolved = (e: { cardId: string; resolution: ActionCardResolution }) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (!m.pendingCard || m.pendingCard.cardId !== e.cardId) {
            return m
          }
          const remnant: ResolvedChatCard = { cardId: e.cardId, resolution: e.resolution }
          return {
            ...m,
            pendingCard: null,
            resolvedCards: [...(m.resolvedCards ?? []), remnant],
          }
        }),
      )
    }

    const onTitle = () => {
      void mutate()
      void globalMutate('/api/recall/conversations')
    }

    const onToolResultClearApproval = (e: { messageId: string; toolUseId: string; status?: string }) =>
      clearApproval({
        messageId: e.messageId,
        toolUseId: e.toolUseId,
        decision: e.status === 'error' ? 'skip' : 'approve',
      })

    // [PROD-1044] Server liveness ping — "the turn is alive, just quiet".
    const onHeartbeat = () => setStreamAliveAt(Date.now())

    // [PROD-1044] Ephemeral tool signal — `slug: null` means the tool finished,
    // so the bar falls back to its generic label. Never persisted: pool chats
    // deliberately hide raw tool detail, and this is the only place their tool
    // activity surfaces.
    const onActivity = (e: { messageId: string; slug: string | null; args?: unknown }) => {
      setLiveActivity(e.slug ? { slug: e.slug, args: e.args } : null)
      setStreamAliveAt(Date.now())
    }

    socket.on('message:start', onStart)
    socket.on('message:queued', onQueued)
    socket.on('message:token', onToken)
    socket.on('message:tool_use', onToolUse)
    socket.on('message:tool_result', onToolResult)
    socket.on('message:tool_result', onToolResultClearApproval)
    socket.on('message:complete', onComplete)
    socket.on('message:error', onError)
    socket.on('message:cancelled', onCancelled)
    socket.on('message:approval_request', onApprovalRequest)
    socket.on('message:card_request', onCardRequest)
    socket.on('message:card_resolved', onCardResolved)
    socket.on('message:heartbeat', onHeartbeat)
    socket.on('message:activity', onActivity)
    socket.on('conversation:title', onTitle)

    return () => {
      socket.off('message:start', onStart)
      socket.off('message:queued', onQueued)
      socket.off('message:token', onToken)
      socket.off('message:tool_use', onToolUse)
      socket.off('message:tool_result', onToolResult)
      socket.off('message:tool_result', onToolResultClearApproval)
      socket.off('message:complete', onComplete)
      socket.off('message:error', onError)
      socket.off('message:cancelled', onCancelled)
      socket.off('message:approval_request', onApprovalRequest)
      socket.off('message:card_request', onCardRequest)
      socket.off('message:card_resolved', onCardResolved)
      socket.off('message:heartbeat', onHeartbeat)
      socket.off('message:activity', onActivity)
      socket.off('conversation:title', onTitle)
      unsubscribeConnected()
      // [PROD-116] Leave the room on switch/unmount — the singleton socket
      // otherwise stays subscribed to every conversation it ever viewed and
      // a still-streaming old turn bleeds its events into the new chat.
      socket.emit('host:leave-conversation', { conversationId: activeId })
    }
  }, [activeId, mutate])

  // Watchdog — 7s mobile / 45s desktop on stuck 'awaiting'. Mobile
  // browsers (especially iOS Safari) sometimes drop socket frames
  // around backgrounding/keyboard transitions; we may have a finished
  // turn server-side and a stale 'awaiting' on the client. Refetch
  // before forcing idle so the user sees what they actually missed.
  //
  // [PROD-1044] `streamAliveAt` is in the deps so every server heartbeat
  // RESTARTS this timer. A pool turn legitimately spends longer than these
  // budgets in 'awaiting' (session create + cold pod-slot boot), and firing
  // there hid the "Generating…" bar while the request was still running. The
  // server stops pinging at its own ceiling, so a genuinely lost stream still
  // recovers here — and heartbeats only ever arrive for a live turn.
  useEffect(() => {
    if (turnState !== 'awaiting') {
      return
    }
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
    const watchdogMs = isMobile ? 7_000 : 45_000
    const timer = setTimeout(() => {
      void mutate()
      setTurnState('idle')
      setMessages((prev) => prev.map((m) => (m.status === 'streaming' ? { ...m, status: 'complete' } : m)))
      console.warn(`[agent-embed] turnState stuck in "awaiting" for ${watchdogMs}ms; refetched + forced idle`)
    }, watchdogMs)
    return () => clearTimeout(timer)
  }, [turnState, streamAliveAt, mutate])

  // Mobile foreground refetch — when the page returns to visible while
  // a conversation is active, refetch messages so any updates that
  // arrived while the socket was paused/dropped land in the UI without
  // requiring a full page refresh. Bug 43474c32.
  useEffect(() => {
    if (typeof document === 'undefined' || !activeId) {
      return
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void mutate()
        // A no-op socket emit catches dead-but-appears-connected sockets;
        // socket.io will reconnect (re-running the auth callback) if the
        // write fails.
        try {
          getEmbedSocket().emit('host:ping')
        } catch {
          /* socket may be transitioning — next visibility cycle handles it */
        }
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [activeId, mutate])

  // Force idle on socket disconnect — composer-deadlock recovery.
  useEffect(() => {
    return onSocketConnectedChange((connected) => {
      if (!connected) {
        setTurnState('idle')
      }
    })
  }, [])

  const ensureConversation = useCallback(async (): Promise<string | null> => {
    if (conversationIdRef.current) {
      return conversationIdRef.current
    }
    try {
      const res = await fetch(urlOf('/api/recall/conversations'), {
        method: 'POST',
        credentials: 'include',
        headers: { ...(await authHeaders()) },
      })
      if (!res.ok) {
        console.warn(`[agent-embed] ensureConversation failed (${res.status}) — file uploads will be skipped`)
        return null
      }
      const created = (await res.json()) as { id: string }
      conversationIdRef.current = created.id
      setActiveId(created.id)
      return created.id
    } catch (err) {
      console.warn('[agent-embed] ensureConversation network error', err)
      return null
    }
  }, [])

  // Per-message retry context — holds the original send args keyed by
  // local optimistic id so retry() can re-post with the same payload.
  // Cleared once the server returns a real messageId or a retry succeeds.
  const retryContextRef = useRef<
    Map<
      string,
      {
        content: string
        hostApp?: string
        attachmentIds?: string[]
        mode?: 'ask' | 'agent'
        slashCommand?: { trigger: string; label: string }
        hostContext?: Record<string, unknown>
        attachments?: ChatAttachment[]
      }
    >
  >(new Map())

  const [lastError, setLastError] = useState<AgentSendError | null>(null)

  // Helper: post a single message turn to the backend. Used by both
  // initial send and retry so the network logic isn't duplicated.
  const postMessage = useCallback(
    async (
      conversationIdLocal: string,
      content: string,
      hostApp?: string,
      attachmentIds?: string[],
      mode?: 'ask' | 'agent',
      hostContext?: Record<string, unknown>,
    ): Promise<{ messageId: string }> => {
      let res: Response
      try {
        res = await fetch(urlOf(`/api/conversations/${conversationIdLocal}/messages`), {
          method: 'POST',
          headers: { 'content-type': 'application/json', ...(await authHeaders()) },
          credentials: 'include',
          body: JSON.stringify({
            content,
            hostApp,
            ...(hostContext ? { hostContext } : {}),
            ...(mode ? { mode } : {}),
            ...(attachmentIds && attachmentIds.length > 0 ? { attachmentIds } : {}),
          }),
        })
      } catch (err) {
        // fetch rejects on offline / DNS / CORS — categorise as network.
        throw new AgentSendError({
          category: 'network',
          message: err instanceof Error ? err.message : 'Network unavailable',
        })
      }
      if (!res.ok) {
        throw new AgentSendError({
          category: classifyHttpStatus(res.status),
          status: res.status,
          message: `send_failed_${res.status}`,
        })
      }
      return (await res.json()) as { messageId: string }
    },
    [],
  )

  const send = useCallback(
    async (
      content: string,
      hostApp?: string,
      attachmentIds?: string[],
      mode?: 'ask' | 'agent',
      slashCommand?: { trigger: string; label: string },
      hostContext?: Record<string, unknown>,
      attachments?: ChatAttachment[],
    ): Promise<string> => {
      // Append the user message OPTIMISTICALLY before any network so
      // the UI flips into chat mode immediately. If create or send
      // fails, we flip the message to status:'error' with a typed
      // error so the surface can offer a Retry button.
      // Attachments ride along so the just-sent bubble renders them
      // immediately — the id swap below means the seed reconcile never
      // touches this row. [PROD-93]
      const optimistic: ChatMessage = {
        id: `optimistic-${Date.now()}`,
        role: 'user',
        content,
        status: 'complete',
        ...(slashCommand ? { slashCommand } : {}),
        ...(attachments && attachments.length > 0 ? { attachments } : {}),
      }
      retryContextRef.current.set(optimistic.id, {
        content,
        ...(hostApp !== undefined ? { hostApp } : {}),
        ...(attachmentIds !== undefined ? { attachmentIds } : {}),
        ...(mode !== undefined ? { mode } : {}),
        ...(slashCommand !== undefined ? { slashCommand } : {}),
        ...(hostContext !== undefined ? { hostContext } : {}),
        ...(attachments !== undefined ? { attachments } : {}),
      })
      setMessages((prev) => [...prev, optimistic])
      setTurnState('sending')

      let id = conversationIdRef.current
      if (!id) {
        try {
          const res = await fetch(urlOf('/api/recall/conversations'), {
            method: 'POST',
            credentials: 'include',
            headers: { ...(await authHeaders()) },
          })
          if (!res.ok) {
            throw new AgentSendError({
              category: classifyHttpStatus(res.status),
              status: res.status,
              message: `create_failed_${res.status}`,
              messageId: optimistic.id,
            })
          }
          const created = (await res.json()) as { id: string }
          id = created.id
          conversationIdRef.current = id
          setActiveId(id)
          getEmbedSocket().emit('host:join-conversation', { conversationId: id })
        } catch (err) {
          const typed =
            err instanceof AgentSendError
              ? err
              : new AgentSendError({
                  category: 'network',
                  message: err instanceof Error ? err.message : 'Could not reach agent backend',
                  messageId: optimistic.id,
                })
          setLastError(typed)
          setTurnState('idle')
          setMessages((prev) =>
            prev.map((m) =>
              m.id === optimistic.id
                ? { ...m, status: 'error', errorMessage: errorMessageFor(typed), errorCategory: typed.category }
                : m,
            ),
          )
          throw typed
        }
      }

      try {
        const { messageId } = await postMessage(id, content, hostApp, attachmentIds, mode, hostContext)
        // Sync optimistic id → real messageId; keep the retry context
        // under the real id so a later retry still works.
        const ctx = retryContextRef.current.get(optimistic.id)
        if (ctx) {
          retryContextRef.current.delete(optimistic.id)
          retryContextRef.current.set(messageId, ctx)
        }
        setMessages((prev) => prev.map((m) => (m.id === optimistic.id ? { ...m, id: messageId } : m)))
        setTurnState((prev) => (prev === 'sending' ? 'awaiting' : prev))
        setLastError(null)
        void mutate()
        return id
      } catch (err) {
        const typed =
          err instanceof AgentSendError
            ? err
            : new AgentSendError({
                category: 'unknown',
                message: err instanceof Error ? err.message : 'Send failed',
                messageId: optimistic.id,
              })
        setLastError(typed)
        setTurnState('idle')
        setMessages((prev) =>
          prev.map((m) =>
            m.id === optimistic.id
              ? { ...m, status: 'error', errorMessage: errorMessageFor(typed), errorCategory: typed.category }
              : m,
          ),
        )
        throw typed
      }
    },
    [mutate, postMessage],
  )

  const retry = useCallback(
    async (messageId: string): Promise<void> => {
      // Branch on message role. Assistant-side error → re-fire the
      // agent loop against the preceding user message id (no need
      // for retry-context — the original user message lives in the
      // RecallMessage table). User-side error → re-`postMessage`
      // with the saved retry-context payload.
      const target = messagesRef.current.find((m) => m.id === messageId)
      if (target?.role === 'assistant') {
        const id = conversationIdRef.current
        if (!id) {
          return
        }
        const idx = messagesRef.current.findIndex((m) => m.id === messageId)
        let userMessageId: string | null = null
        for (let i = idx - 1; i >= 0; i--) {
          const m = messagesRef.current[i]
          if (m && m.role === 'user') {
            userMessageId = m.id
            break
          }
        }
        if (!userMessageId) {
          console.warn('[use-conversation] assistant-side retry: no preceding user message found')
          return
        }
        // Drop the failed assistant bubble before re-firing — the new
        // run's `message:start` will append a fresh row.
        setMessages((prev) => prev.filter((m) => m.id !== messageId))
        setTurnState('awaiting')
        try {
          const r = await fetch(urlOf(`/api/conversations/${id}/messages/${userMessageId}/retry`), {
            method: 'POST',
            credentials: 'include',
            headers: { ...(await authHeaders()) },
          })
          if (!r.ok) {
            throw new Error(`retry_failed_${r.status}`)
          }
        } catch (err) {
          setTurnState('idle')
          console.warn('[use-conversation] assistant-side retry failed', err)
        }
        return
      }
      const ctx = retryContextRef.current.get(messageId)
      if (!ctx) {
        // No retry context — message was loaded from server, can't
        // re-send without re-fetching the original payload. (Future:
        // fetch from /api/conversations/:id/messages/:messageId.)
        return
      }
      // Flip the message back to 'complete' (no error pill) before
      // the network spin so the UI feels immediate.
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, status: 'complete', errorMessage: undefined } : m)),
      )
      setTurnState('sending')

      let convId = conversationIdRef.current
      if (!convId) {
        try {
          const res = await fetch(urlOf('/api/recall/conversations'), {
            method: 'POST',
            credentials: 'include',
            headers: { ...(await authHeaders()) },
          })
          if (!res.ok) {
            throw new AgentSendError({
              category: classifyHttpStatus(res.status),
              status: res.status,
              message: `create_failed_${res.status}`,
              messageId,
            })
          }
          const created = (await res.json()) as { id: string }
          convId = created.id
          conversationIdRef.current = convId
          setActiveId(convId)
          getEmbedSocket().emit('host:join-conversation', { conversationId: convId })
        } catch (err) {
          const typed =
            err instanceof AgentSendError
              ? err
              : new AgentSendError({
                  category: 'network',
                  message: err instanceof Error ? err.message : 'Could not reach agent backend',
                  messageId,
                })
          setLastError(typed)
          setTurnState('idle')
          setMessages((prev) =>
            prev.map((m) =>
              m.id === messageId
                ? { ...m, status: 'error', errorMessage: errorMessageFor(typed), errorCategory: typed.category }
                : m,
            ),
          )
          return
        }
      }

      try {
        const { messageId: serverId } = await postMessage(
          convId,
          ctx.content,
          ctx.hostApp,
          ctx.attachmentIds,
          ctx.mode,
          ctx.hostContext,
        )
        if (serverId !== messageId) {
          // Server assigned a new id — update the local message + ctx key.
          retryContextRef.current.delete(messageId)
          retryContextRef.current.set(serverId, ctx)
          setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, id: serverId } : m)))
        }
        setTurnState((prev) => (prev === 'sending' ? 'awaiting' : prev))
        setLastError(null)
        void mutate()
      } catch (err) {
        const typed =
          err instanceof AgentSendError
            ? err
            : new AgentSendError({
                category: 'unknown',
                message: err instanceof Error ? err.message : 'Send failed',
                messageId,
              })
        setLastError(typed)
        setTurnState('idle')
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId
              ? { ...m, status: 'error', errorMessage: errorMessageFor(typed), errorCategory: typed.category }
              : m,
          ),
        )
      }
    },
    [mutate, postMessage],
  )

  const conversation = useMemo(() => data ?? null, [data])

  const cancel = useCallback(async (): Promise<void> => {
    const id = conversationIdRef.current
    if (!id) {
      return
    }
    setTurnState('idle')
    setMessages((prev) => prev.map((m) => (m.status === 'streaming' ? { ...m, status: 'complete' } : m)))
    await fetch(urlOf(`/api/conversations/${id}/cancel`), {
      method: 'POST',
      credentials: 'include',
      headers: { ...(await authHeaders()) },
    })
  }, [])

  const decideApproval = useCallback(async (toolUseId: string, decision: 'approve' | 'skip'): Promise<void> => {
    const id = conversationIdRef.current
    if (!id) {
      return
    }
    await fetch(urlOf(`/api/conversations/${id}/approve`), {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...(await authHeaders()) },
      credentials: 'include',
      body: JSON.stringify({ toolUseId, decision }),
    })
  }, [])

  const resolveCard = useCallback(async (cardId: string, resolution: ActionCardResolution): Promise<void> => {
    const id = conversationIdRef.current
    if (!id) {
      return
    }
    await fetch(urlOf(`/api/conversations/${id}/cards/${cardId}/resolve`), {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...(await authHeaders()) },
      credentials: 'include',
      body: JSON.stringify(resolution),
    })
  }, [])

  const loadOlder = useCallback(async () => {
    const id = conversationIdRef.current
    if (!id || !hasMore || isLoadingOlder) {
      return
    }
    setIsLoadingOlder(true)
    try {
      const oldest = messages[0]
      if (!oldest) {
        return
      }
      const res = await fetch(
        urlOf(`/api/recall/conversations/${id}?before=${encodeURIComponent(oldest.id)}&limit=50`),
        { credentials: 'include' },
      )
      if (!res.ok) {
        return
      }
      const payload = (await res.json()) as PersistedConversation
      const older: ChatMessage[] = payload.messages.map((m) => ({
        id: m.id,
        role: m.role.toLowerCase() === 'user' ? 'user' : 'assistant',
        content: m.content,
        toolCalls: Array.isArray(m.toolCalls) ? m.toolCalls : undefined,
        citations: Array.isArray(m.citations) ? m.citations : undefined,
        attachments: Array.isArray(m.attachments) ? m.attachments : undefined,
        status: 'complete',
        tokensIn: m.tokensIn ?? undefined,
        tokensOut: m.tokensOut ?? undefined,
        costUsd: typeof m.costUsd === 'string' ? Number(m.costUsd) : (m.costUsd ?? undefined),
      }))
      setMessages((prev) => [...older, ...prev])
      setHasMore(Boolean(payload.hasMore))
    } finally {
      setIsLoadingOlder(false)
    }
  }, [hasMore, isLoadingOlder, messages])

  return {
    conversation,
    messages,
    hasMore,
    isLoadingOlder,
    loadOlder,
    isStreaming,
    turnState,
    isBusy,
    isLoading,
    liveActivity,
    send,
    retry,
    cancel,
    decideApproval,
    resolveCard,
    ensureConversation,
    conversationId: conversationIdRef.current,
    lastError,
  }
}
